import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Scale = () => {
  const { t } = useLanguage();

  const icons = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Multi-City Operations',
      description: 'Designed for deployment across multiple cities and regions with centralized management and localized operations.',
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: 'Enterprise & Government',
      description: 'Ready for large-scale contracts with construction companies, industrial facilities, and government projects.',
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Compliance Ready',
      description: 'Built to meet regulatory requirements for water logistics, with full audit trails and documentation.',
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'API Integration',
      description: 'Connect Watrly with existing systems through our documented APIs for seamless data exchange.',
    },
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-watrly-slate via-watrly-slate-light to-watrly-slate relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-watrly-turquoise/12 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-watrly-desert-gold/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-watrly-aqua/8 rounded-full blur-3xl" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-watrly-turquoise/20 to-transparent" />

      <div className="container-watrly relative">
        {/* Mobile: Image as background with content overlay */}
        <div className="lg:hidden relative">
          {/* Background Image */}
          <div className="absolute inset-0 -mx-6 rounded-2xl overflow-hidden opacity-20">
            <img
              src="/assets/images/Image_202601192023.jpeg"
              alt="Watrly water infrastructure facility"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-watrly-slate via-watrly-slate/90 to-watrly-slate" />
          </div>
          
          {/* Content */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-6"
            >
              <span className="inline-block text-xs font-semibold text-watrly-turquoise uppercase tracking-widest mb-2">
                {t.scale.badge}
              </span>
              <h2 className="text-xl font-bold text-white tracking-tight mb-2">
                {t.scale.title}
              </h2>
              <p className="text-sm text-white/60 leading-relaxed">
                {t.scale.subtitle}
              </p>
            </motion.div>

            {/* 2-column grid for cards on mobile */}
            <div className="grid grid-cols-2 gap-3">
              {t.scale.capabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10"
                >
                  <div className="w-8 h-8 rounded-lg bg-watrly-turquoise/20 flex items-center justify-center text-watrly-turquoise mb-2">
                    {icons[index]?.icon}
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1">
                    {capability.title}
                  </h3>
                  <p className="text-xs text-white/50 leading-relaxed line-clamp-2">
                    {capability.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Original 2-column layout */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold text-watrly-turquoise uppercase tracking-widest mb-4">
              {t.scale.badge}
            </span>
            <h2 className="text-display-sm font-bold text-white tracking-tight mb-6">
              {t.scale.title}
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-6">
              {t.scale.subtitle}
            </p>

            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="/assets/images/Image_202601192023.jpeg"
                alt="Watrly water infrastructure facility"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-watrly-slate via-transparent to-transparent" />
            </div>
          </motion.div>

          <div className="space-y-6">
            {t.scale.capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-watrly-turquoise/30 transition-all duration-300"
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-watrly-turquoise/20 flex items-center justify-center text-watrly-turquoise group-hover:bg-watrly-turquoise group-hover:text-white transition-all duration-300">
                    {icons[index]?.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {capability.title}
                    </h3>
                    <p className="text-base text-white/50 leading-relaxed">
                      {capability.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 md:mt-16 pt-6 md:pt-12 border-t border-white/10"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {[
              { value: '50+', label: t.scale.stats.stations },
              { value: '200+', label: t.scale.stats.drivers },
              { value: '10K+', label: t.scale.stats.deliveries },
              { value: '99.2%', label: t.scale.stats.success },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-2xl md:text-4xl lg:text-5xl font-bold text-watrly-turquoise mb-1 md:mb-2">
                  {stat.value}
                </div>
                <div className="text-xs md:text-base text-white/50 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Scale;
